// frontend/src/super-admin/site/HelpDocEditorPage.tsx
//
// Create / edit a single help center article. Slug is editable on create only.
// SEO overrides live on the same form (per-doc seo_title / seo_description).

import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { PageHeader } from '../../design-system/components/PageHeader';
import { LoadingState } from '../../design-system/components/LoadingState';
import { ErrorState } from '../../design-system/components/ErrorState';
import { VISUAL, statusPill } from '../../design-system/visual';
import { docsApi, type HelpDoc, type HelpDocInput } from '../../modules/site-settings/api';
import { useAdminPermission } from '../../core/auth/useAdminPermission';

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
}

export function HelpDocEditorPage() {
  const canWrite = useAdminPermission('site.docs.write');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [doc, setDoc] = React.useState<HelpDoc | null>(null);
  const [draft, setDraft] = React.useState<HelpDocInput>({
    slug: '',
    title: '',
    excerpt: '',
    body_md: '',
    category: 'General',
    sort_order: 100,
    seo_title: '',
    seo_description: '',
    is_published: false,
  });
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [slugTouched, setSlugTouched] = React.useState(false);

  React.useEffect(() => {
    if (isNew) return;
    setError(null);
    docsApi.get(id!)
      .then((d) => {
        setDoc(d);
        setDraft({
          slug: d.slug,
          title: d.title,
          excerpt: d.excerpt ?? '',
          body_md: d.body_md,
          category: d.category,
          sort_order: d.sort_order,
          seo_title: d.seo_title ?? '',
          seo_description: d.seo_description ?? '',
          is_published: d.is_published,
        });
        setSlugTouched(true);
      })
      .catch((e) => setError(String(e.message ?? e)));
  }, [id, isNew]);

  // Auto-slug from title in create mode until the user touches the slug.
  React.useEffect(() => {
    if (isNew && !slugTouched && draft.title) {
      setDraft((d) => ({ ...d, slug: slugify(d.title ?? '') }));
    }
  }, [draft.title, isNew, slugTouched]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload: HelpDocInput = {
        ...draft,
        title: (draft.title ?? '').trim(),
        slug: (draft.slug ?? '').trim(),
        category: (draft.category ?? 'General').trim() || 'General',
        sort_order: typeof draft.sort_order === 'number' ? draft.sort_order : 100,
      };

      if (isNew) {
        const created = await docsApi.create(payload);
        toast.success('Article created');
        navigate(`/admin/site/docs/${created.id}`);
      } else {
        const updated = await docsApi.update(id!, payload);
        setDoc(updated);
        toast.success('Article saved');
      }
    } catch (err: any) {
      toast.error('Save failed', { description: err.message ?? String(err) });
    } finally { setBusy(false); }
  }

  if (!canWrite && !isNew) {
    return (
      <div className="px-6 sm:px-8 lg:px-10 py-16">
        <div className={[VISUAL.bento, 'p-8 max-w-md mx-auto text-center'].join(' ')}>
          <p className="text-[16px] font-semibold text-foreground">Read-only</p>
          <p className="text-[13.5px] text-muted-foreground mt-1">You don't have site.docs.write permission.</p>
        </div>
      </div>
    );
  }

  if (!isNew && !doc && !error) return <LoadingState variant="page" />;
  if (error) return <ErrorState description={error} />;

  return (
    <div>
      <PageHeader
        eyebrow="Site / Content"
        title={isNew ? 'New help article' : (doc?.title || 'Edit article')}
        description={isNew ? "Draft a new article. Publish when you're ready." : `/${doc?.slug ?? ''}`}
        actions={
          <Button type="button" variant="ghost" onClick={() => navigate('/admin/site/docs')} className="rounded-full h-10 px-4 gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to help center
          </Button>
        }
      />

      <form onSubmit={save} className="px-6 sm:px-8 lg:px-10 pb-12 grid grid-cols-1 xl:grid-cols-3 gap-6 max-w-6xl">
        {/* Main column */}
        <div className="xl:col-span-2 space-y-6">
          <section className={[VISUAL.bento, 'p-6 sm:p-7 space-y-5'].join(' ')}>
            <div className="flex items-center gap-3 mb-1">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/15 ring-1 ring-coral/25">
                <FileText className="h-4 w-4 text-coral" strokeWidth={1.7} />
              </div>
              <h3 className={VISUAL.dashSection}>Article</h3>
            </div>

            <div className="grid gap-1.5">
              <Label>Title *</Label>
              <Input
                value={draft.title ?? ''}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                required
                placeholder="Getting started with invoicing"
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Slug *</Label>
              <Input
                value={draft.slug ?? ''}
                onChange={(e) => { setSlugTouched(true); setDraft({ ...draft, slug: e.target.value }); }}
                required
                placeholder="getting-started-invoicing"
                pattern="[a-z0-9](?:[a-z0-9-]{0,98}[a-z0-9])?"
              />
              <p className="text-[12px] text-muted-foreground">Lowercase letters, digits, and dashes. Used in the URL: /docs/&lt;slug&gt;</p>
            </div>

            <div className="grid gap-1.5">
              <Label>Excerpt</Label>
              <Textarea
                value={draft.excerpt ?? ''}
                onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
                rows={2}
                placeholder="One-sentence summary shown on the docs index."
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Body (Markdown)</Label>
              <Textarea
                value={draft.body_md ?? ''}
                onChange={(e) => setDraft({ ...draft, body_md: e.target.value })}
                rows={18}
                placeholder="## Overview&#10;&#10;This guide explains how to…"
                className="font-mono text-[13px]"
              />
            </div>
          </section>

          <section className={[VISUAL.bento, 'p-6 sm:p-7 space-y-5'].join(' ')}>
            <div className="flex items-center gap-3 mb-1">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-lavender-100 ring-1 ring-lavender-200">
                <Search className="h-4 w-4 text-lavender-700" strokeWidth={1.7} />
              </div>
              <h3 className={VISUAL.dashSection}>SEO overrides</h3>
            </div>

            <div className="grid gap-1.5">
              <Label>SEO title</Label>
              <Input
                value={draft.seo_title ?? ''}
                onChange={(e) => setDraft({ ...draft, seo_title: e.target.value })}
                placeholder="Defaults to the article title"
                maxLength={70}
              />
              <p className="text-[12px] text-muted-foreground">{(draft.seo_title ?? '').length}/70 characters</p>
            </div>

            <div className="grid gap-1.5">
              <Label>SEO description</Label>
              <Textarea
                value={draft.seo_description ?? ''}
                onChange={(e) => setDraft({ ...draft, seo_description: e.target.value })}
                rows={2}
                placeholder="Defaults to the excerpt"
                maxLength={160}
              />
              <p className="text-[12px] text-muted-foreground">{(draft.seo_description ?? '').length}/160 characters</p>
            </div>
          </section>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <section className={[VISUAL.bento, 'p-6 space-y-4'].join(' ')}>
            <div className="flex items-center justify-between">
              <Label>Status</Label>
              <span className={statusPill(draft.is_published ? 'success' : 'draft')}>
                {draft.is_published ? 'Published' : 'Draft'}
              </span>
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDraft({ ...draft, is_published: !draft.is_published })}
              className={
                'w-full h-10 rounded-full text-[13px] font-semibold ' +
                (draft.is_published
                  ? 'bg-muted/60 text-muted-foreground'
                  : 'bg-coral text-white hover:bg-coral/90')
              }
            >
              {draft.is_published ? 'Move to draft' : 'Publish on save'}
            </Button>
          </section>

          <section className={[VISUAL.bento, 'p-6 space-y-4'].join(' ')}>
            <div className="grid gap-1.5">
              <Label>Category</Label>
              <Input
                value={draft.category ?? 'General'}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                placeholder="General"
              />
              <p className="text-[12px] text-muted-foreground">Groups this article on the /docs index.</p>
            </div>

            <div className="grid gap-1.5">
              <Label>Sort order</Label>
              <Input
                type="number"
                value={draft.sort_order ?? 100}
                onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) })}
                placeholder="100"
                min={0}
                step={10}
              />
              <p className="text-[12px] text-muted-foreground">Lower numbers appear first within the category.</p>
            </div>
          </section>

          <Button
            type="submit"
            disabled={busy}
            className="w-full h-11 rounded-full text-[14px] font-semibold"
          >
            {busy ? 'Saving…' : isNew ? 'Create article' : 'Save changes'}
          </Button>
        </aside>
      </form>
    </div>
  );
}
